import apiClient from './client';

// ===== WHATSAPP MESSAGES =====

/**
 * Send receipt via WhatsApp
 * @param {Object} data - Send receipt data
 * @param {number} data.saleId - Sale ID
 * @param {string} data.phoneNumber - Recipient phone number
 * @returns {Promise<Object>} Response with message details
 */
export const sendReceipt = async (data) => {
  const response = await apiClient.post('/whatsapp/send-receipt', data);
  return response.data;
};

/**
 * Get WhatsApp message history with filters and pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 20)
 * @param {string} params.status - Filter by status (pending, sent, delivered, read, failed)
 * @param {string} params.startDate - Start date filter (YYYY-MM-DD)
 * @param {string} params.endDate - End date filter (YYYY-MM-DD)
 * @param {number} params.saleId - Filter by sale ID
 * @param {string} params.phone - Filter by phone number
 * @returns {Promise<Object>} Response with messages and pagination
 */
export const getMessageHistory = async (params = {}) => {
  const response = await apiClient.get('/whatsapp/messages', {
    params,
    headers: {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }
  });
  return response.data;
};

/**
 * Get single WhatsApp message by ID
 * @param {number} id - Message ID
 * @returns {Promise<Object>} Response with message details
 */
export const getMessageById = async (id) => {
  const response = await apiClient.get(`/whatsapp/messages/${id}`, {
    headers: {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }
  });
  return response.data;
};

/**
 * Retry failed WhatsApp message
 * @param {number} id - Message ID
 * @returns {Promise<Object>} Response with retry result
 */
export const retryMessage = async (id) => {
  const response = await apiClient.post(`/whatsapp/messages/${id}/retry`);
  return response.data;
};

/**
 * Bulk retry multiple failed messages
 * @param {Array<number>} messageIds - Array of message IDs to retry
 * @returns {Promise<Object>} Response with bulk retry results
 */
export const bulkRetryMessages = async (messageIds) => {
  const response = await apiClient.post('/whatsapp/messages/bulk-retry', {
    messageIds
  });
  return response.data;
};

/**
 * Get WhatsApp configuration status (admin only)
 * @returns {Promise<Object>} Response with configuration status
 */
export const getConfigStatus = async () => {
  const response = await apiClient.get('/whatsapp/config/status');
  return response.data;
};

// ===== WHATSAPP SETTINGS =====

/**
 * Get WhatsApp settings for a specific module
 * @param {string} module - Module name ('retail' or 'wholesale')
 * @returns {Promise<Object>} Response with settings
 */
export const getSettings = async (module) => {
  const response = await apiClient.get('/whatsapp/settings', {
    params: { module }
  });
  return response.data;
};

/**
 * Update WhatsApp settings
 * @param {Object} data - Settings data including module
 * @returns {Promise<Object>} Response with updated settings
 */
export const updateSettings = async (data) => {
  const response = await apiClient.put('/whatsapp/settings', data);
  return response.data;
};

// ===== UTILITY FUNCTIONS =====

/**
 * Get message status color
 * @param {string} status - Message status
 * @returns {string} Color hex code
 */
export const getMessageStatusColor = (status) => {
  const statusColors = {
    pending: '#F59E0B',    // Yellow/Gold
    sent: '#3B82F6',       // Blue
    delivered: '#10B981',  // Green
    read: '#059669',       // Dark Green
    failed: '#EF4444'      // Red
  };
  return statusColors[status] || '#6B7280';
};

/**
 * Get message status icon
 * @param {string} status - Message status
 * @returns {string} Icon name or emoji
 */
export const getMessageStatusIcon = (status) => {
  const statusIcons = {
    pending: '⏳',
    sent: '✈️',
    delivered: '✓✓',
    read: '✓✓', // Same as delivered but different color
    failed: '❌'
  };
  return statusIcons[status] || '•';
};

/**
 * Get message status label
 * @param {string} status - Message status
 * @returns {string} Human-readable status label
 */
export const getMessageStatusLabel = (status) => {
  const statusLabels = {
    pending: 'Pending',
    sent: 'Sent',
    delivered: 'Delivered',
    read: 'Read',
    failed: 'Failed'
  };
  return statusLabels[status] || status;
};

/**
 * Format phone number for display
 * @param {string} phone - Phone number
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return 'N/A';

  // Remove any non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Format Nigerian numbers: +234 801 234 5678
  if (digits.startsWith('234') && digits.length === 13) {
    return `+234 ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
  }

  // Format local Nigerian numbers: 0801 234 5678
  if (digits.startsWith('0') && digits.length === 11) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }

  // Return as is if doesn't match expected formats
  return phone;
};

/**
 * Validate Nigerian phone number
 * @param {string} phone - Phone number to validate
 * @returns {Object} Validation result {isValid: boolean, message: string}
 */
export const validatePhoneNumber = (phone) => {
  if (!phone || phone.trim() === '') {
    return { isValid: false, message: 'Phone number is required' };
  }

  // Remove any non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Complete list of valid Nigerian mobile prefixes (matching backend)
  const validPrefixes = [
    // MTN Nigeria
    '703', '704', '706', '707', '803', '806', '810', '813', '814', '816', '903', '904', '906', '913', '916',
    // Airtel Nigeria
    '701', '708', '802', '808', '812', '901', '902', '907', '912',
    // Glo Nigeria
    '705', '805', '807', '811', '815', '905', '915',
    // 9mobile Nigeria
    '809', '817', '818', '908', '909',
    // All 8xx and 9xx series for completeness
    '801', '802', '803', '804', '805', '806', '807', '808', '809',
    '810', '811', '812', '813', '814', '815', '816', '817', '818', '819',
    '901', '902', '903', '904', '905', '906', '907', '908', '909',
    '910', '911', '912', '913', '914', '915', '916'
  ];

  // Check if it's a valid Nigerian mobile number
  if (digits.length === 11 && digits.startsWith('0')) {
    // Local format: 08012345678
    const prefix = digits.slice(1, 4);
    if (validPrefixes.includes(prefix)) {
      return { isValid: true, message: 'Valid phone number' };
    } else {
      return { isValid: false, message: 'Invalid phone number prefix' };
    }
  } else if (digits.length === 13 && digits.startsWith('234')) {
    // International format: 2348012345678
    const prefix = digits.slice(3, 6);
    if (validPrefixes.includes(prefix)) {
      return { isValid: true, message: 'Valid phone number' };
    } else {
      return { isValid: false, message: 'Invalid phone number prefix' };
    }
  } else if (digits.length === 10) {
    // Missing leading 0: 8012345678
    const prefix = digits.slice(0, 3);
    if (validPrefixes.includes(prefix)) {
      return { isValid: true, message: 'Valid phone number' };
    } else {
      return { isValid: false, message: 'Invalid phone number prefix' };
    }
  } else {
    return {
      isValid: false,
      message: 'Phone number must be 11 digits (e.g., 08012345678) or in international format (+2348012345678)'
    };
  }
};

/**
 * Normalize phone number to international format
 * @param {string} phone - Phone number to normalize
 * @returns {string} Normalized phone number (+2348012345678)
 */
export const normalizePhoneNumber = (phone) => {
  if (!phone) return '';

  // Remove any non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Convert local format to international (11 digits starting with 0)
  if (digits.length === 11 && digits.startsWith('0')) {
    return `+234${digits.slice(1)}`;
  }

  // Handle 10-digit format (missing leading 0)
  if (digits.length === 10) {
    return `+234${digits}`;
  }

  // Add + if missing (13 digits starting with 234)
  if (digits.length === 13 && digits.startsWith('234')) {
    return `+${digits}`;
  }

  // Return as is if already in international format
  return phone.startsWith('+') ? phone : `+${digits}`;
};

/**
 * Check if message can be retried
 * @param {Object} message - Message object
 * @returns {boolean} True if message can be retried
 */
export const canRetryMessage = (message) => {
  if (!message) return false;
  return message.message_status === 'failed' &&
         message.retry_count < message.max_retry_attempts;
};

/**
 * Get retry count display
 * @param {Object} message - Message object
 * @returns {string} Retry count display string
 */
export const getRetryCountDisplay = (message) => {
  if (!message) return '';
  return `${message.retry_count || 0} / ${message.max_retry_attempts || 3}`;
};

/**
 * Calculate time since message was sent
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Human-readable time difference
 */
export const getTimeSince = (timestamp) => {
  if (!timestamp) return 'N/A';

  const now = new Date();
  const sent = new Date(timestamp);
  const diffMs = now - sent;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
};

/**
 * Format timestamp for display
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Formatted timestamp
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'N/A';

  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Get network provider from phone number
 * @param {string} phone - Phone number
 * @returns {string} Network provider name
 */
export const getNetworkProvider = (phone) => {
  if (!phone) return 'Unknown';

  const digits = phone.replace(/\D/g, '');
  let prefix;

  if (digits.length === 11 && digits.startsWith('0')) {
    prefix = digits.slice(1, 4);
  } else if (digits.length === 13 && digits.startsWith('234')) {
    prefix = digits.slice(3, 6);
  } else {
    return 'Unknown';
  }

  const mtnPrefixes = ['803', '806', '810', '813', '814', '816', '903', '906'];
  const airtelPrefixes = ['802', '808', '812', '901', '902', '907', '912'];
  const gloPrefixes = ['805', '807', '811', '815', '905', '915'];
  const nineMobilePrefixes = ['809', '817', '818', '909', '908'];

  if (mtnPrefixes.includes(prefix)) return 'MTN';
  if (airtelPrefixes.includes(prefix)) return 'Airtel';
  if (gloPrefixes.includes(prefix)) return 'Glo';
  if (nineMobilePrefixes.includes(prefix)) return '9mobile';

  return 'Unknown';
};
